import useValidation from "../../hooks/useValidation";
import { useEffect, useState } from "react";
import { format, parse, isValid } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { DateType } from "../../static/types/commonTypes/dateType";
import dateValidationSchema from "../../utils/validation/dateValidationSchema";
import FieldDescription from "../tools/FieldDescription";
import useAppContext from "../../hooks/useAppContext";
import { AppContextType } from "../../context/AppContext";
import { useDraftManager } from "../../hooks/useDraftManager";

type dateProps = {
    id?: string,
    dateLabel: string,
    checked?: boolean,
    required: boolean,
    date: DateType,
    draftFieldName?: string,
    changeDate: (date: Partial<DateType>) => void,
    onValidationChange?: (isValid: boolean) => void,
};

export default function DateComponent({
    id,
    checked,
    date,
    dateLabel,
    required,
    draftFieldName,
    changeDate,
    onValidationChange
}: dateProps) {

    const { errors, isValid: isDataValid } = useValidation(date, dateValidationSchema)
    const { draftFormData, setDraftFormData } = useAppContext() as AppContextType

    const stringifyDraft = ( data: any ) => { return JSON.stringify( data )}
    const parseDraft: any = () => { return draftFormData ? JSON.parse( draftFormData ) : {} }

    useEffect(() => {

        if( onValidationChange) onValidationChange(isDataValid);

        const fieldName = draftFieldName ?? 'date'
        const draft = parseDraft()
        const tempDraft = { ...draft, [ fieldName ]: date }
        setDraftFormData( stringifyDraft( tempDraft ))

    }, [isDataValid, onValidationChange]);

    const [selectedDate, setSelectedDate] = useState<string>("");
    const [selectedEffectiveDate, setSelectedEffectiveDate] = useState<string>("");

    useEffect(() => {
        if (date.date) {
            const parsedDate = parse(date.date, "dd/MM/yyyy", new Date());
            setSelectedDate(isValid(parsedDate) ? format(parsedDate, "yyyy-MM-dd") : "");
        }
        if (date.effectiveDate) {
            const parsedDate = parse(date.effectiveDate, "dd/MM/yyyy", new Date());
            setSelectedEffectiveDate(isValid(parsedDate) ? format(parsedDate, "yyyy-MM-dd") : "");
        }
    }, [date]);

    const handleDecisionDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputDate = e.target.value;
    if (!inputDate) {
        setSelectedDate("");
        changeDate({ date: "" });
        return;
    }

    const parsedDate = parse(inputDate, "yyyy-MM-dd", new Date());

    if (!isValid(parsedDate)) {
        setSelectedDate("");
        changeDate({ date: "" });
        return;
    }

    const formattedDate = format(parsedDate, "dd/MM/yyyy");
    setSelectedDate(inputDate);
    changeDate({ date: formattedDate });
};


const handleEffectiveDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputDate = e.target.value;
    if (!inputDate) {
        setSelectedEffectiveDate("");
        changeDate({ effectiveDate: "" });
        return;
    }

    const parsedDate = parse(inputDate, "yyyy-MM-dd", new Date());

    if (!isValid(parsedDate)) {
        setSelectedEffectiveDate("");
        changeDate({ effectiveDate: "" });
        return;
    }

    const formattedDate = format(parsedDate, "dd/MM/yyyy");
    setSelectedEffectiveDate(inputDate);
    changeDate({ effectiveDate: formattedDate });
};


    const openDatePicker = (id: string = "date") => {
        const input = document.getElementById(id) as HTMLInputElement;
        input?.showPicker();
    };

    return (
        <div className="container p-4">
            <div className="row mb-2">
                <label htmlFor="date" className="col-4 col-form-label fw-bold mb-1" style={{marginRight: '-100px'}}>
                    {dateLabel} {required && <strong className="text-danger">*</strong>}
                </label>

                <div className="col-6" style={{marginLeft: '100px'}}>
                    <input
                        className={`description-input form-control ${(checked && errors.date) ? 'is-invalid' : ''}`}
                        type="date"
                        id={id}
                        value={selectedDate}
                        onChange={handleDecisionDateChange}
                        onClick={() => openDatePicker( id )}
                        placeholder="JJ/MM/AAAA"
                    />
                    {(checked && errors.date) && <div className="invalid-feedback">{errors.date}</div>}
                    <FieldDescription description="Format : JJ/MM/AAAA" />
                </div>

                {('effectiveDate' in date) && (
                    <>
                        <label htmlFor="effectiveDate" className="col-4 col-form-label fw-bold mb-1">
                            Date d’effet
                        </label>
                        <div className="col-6">
                            <input
                                className="form-control"
                                type="date"
                                id="effectiveDate"
                                value={selectedEffectiveDate}
                                onChange={handleEffectiveDateChange}
                                onClick={() => openDatePicker("effectiveDate")}
                                placeholder="JJ/MM/AAAA"
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
